import React, {useEffect, useMemo, useState} from "react";
import Tr from "translations/Translations";
import PropTypes from "prop-types";
import Dialog from "components/Dialog";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import Loading from "components/Loading";
import GridBlock from "components/GridBlock";
import {makeStyles} from "@material-ui/core";
import Form from "components/Form";
import useId from "hooks/useId";
import VS from "constants/ValidationShema";
import DateTimePicker from "components/DateTimePicker";
import TypographyWithIcon from "components/TypographyWithIcon";
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {connect} from "react-redux";
import AuctionService from "services/AuctionService";
import Select, {createSelectItem} from "components/Select";
import {AuctionTypeItems} from "constants/AuctionType";
import {AuctionRule, AuctionTypeRuleItems} from "constants/AuctionRule";
import TextInput from "components/TextInput";
import INPUT_TYPE from "constants/INPUT_TYPE";
import {PriceType, PriceTypeItems, PriceTypeItemsForAuction} from "constants/PriceTypes";
import BooleanItems from "constants/BooleanItems";
import Typography from "components/Typography";
import moment from "libs/moment";
import {AuctionItemBidType, AuctionItemBidTypeItems} from "../../constants/AuctionItemBidType";
import OfferChoiceCreateAuctionTable from "./OfferChoiceCreateAuctionTable";
import isResponseValid from "libs/isResponseValid";
import getObjectValue from 'libs/getObjectValue';
import clsx from "clsx";

const JAVA_INTEGER_MAX = 2_147_483_647;
const useStyle = makeStyles(theme => ({
    title: {
        marginTop: 0
    },
    italic: {
        fontStyle: 'italic'
    },
    bold: {
        fontWeight: theme.typography.fontWeightBold
    },
    formElement: {
        width: 400
    },
    dd: {
        whiteSpace: "pre-line",
        fontSize: 9
    },
    hidden: {
        display: 'none'
    }

}));


const OfferChoiceCreateAuctionPopup = (
    {
        demandId,
        onCancel,
        onSuccess,
        roundId,
        auctionType,
        formSubmit,
    }) => {

    const classes = useStyle();

    const [preparedAuction, setPreparedAuction] = useState(null);
    const [working, setWorking] = useState(true);
    const [error, setError] = useState(null);
    const formId = useId();
    const handleSubmit = () => formSubmit[formId]();

    const onSubmit = (values) => {
        setError(null);
        setWorking(true);
        AuctionService.create(demandId, roundId, values).then((resp) => {
            if (isResponseValid(resp, setError)) {
                onSuccess(resp);
            } else {
                setWorking(false);
            }
        }).catch(() => {
            setError(Tr('unknown_error_message'));
            setWorking(false);
        })
    }

    useEffect(() => {
        AuctionService.prepare(demandId, roundId)
            .then(resp => {
                resp.type = auctionType;
                resp.startDate = moment(resp.startDate);
                resp.endDate = moment(resp.endDate);
                resp.positions[0].bidType = AuctionItemBidType.RFX_CRITERIUM_VALUE;
                setPreparedAuction(resp);
            }).finally(() => setWorking(false));
    }, [demandId, roundId, auctionType]);

    const handleUseNetOverGrossChange = (formikProps, value) => {
        AuctionService.prepare(demandId, roundId, value.target.value)
            .then(resp => {
                resp.type = getObjectValue('type', formikProps.values, "");
                resp.rule = getObjectValue('rule', formikProps.values, "");
                resp.startDate = getObjectValue('startDate', formikProps.values, "");
                resp.decisionTime = getObjectValue('decisionTime', formikProps.values, "");
                resp.endDate = getObjectValue('endDate', formikProps.values, "");
                const position = getObjectValue('positions', formikProps.values, "");
                resp.positions[0].bidType = getObjectValue('bidType', position[0], "");
                resp.digitalSign = getObjectValue('digitalSign', formikProps.values, "");
                resp.overtimeCount = getObjectValue('overtimeCount', formikProps.values, "");
                resp.overtime = getObjectValue('overtime', formikProps.values, "");
                setPreparedAuction(resp);
            }).finally(() => setWorking(false));
    }

    const handleEndDate = (formikProps, value) => {
        formikProps.setFieldValue('endDate', value);
        formikProps.setFieldTouched('endDate', true);
    }

    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr('cancel'), onCancel, 'outlined', working));
        buttons.push(createButton(Tr('auction_create_button'), handleSubmit, undefined, working));
        return buttons;
    }

    const handleEnableOvertimeChange = (formikProps, e) => {
        const isEnabled = e.target.value === true || e.target.value === 'true';
        if (isEnabled) {
            formikProps.setFieldValue('overtimeCount', JAVA_INTEGER_MAX);
        } else {
            formikProps.setFieldValue('overtimeCount', 0);
            formikProps.setFieldValue('overtime', 0);
        }
    }


    const validationSchema = VS.lazy((value) => {

        const rule = value.rule;
        const overtimeCount = value.overtimeCount;
        const bidType = value.positions[0].bidType;
        const enableOvertime = value.enableOvertime;
        const shape = {
            rule: VS.string(),
            startDate: VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past')),
            type: VS.string(),

        }

        if (AuctionRule.JAPANESE !== rule) {
            shape.endDate = VS.dateTimeValid()
                .when('startDate', (startDate, schema) => {
                    return schema.test('EndDateLessStartDate', Tr("EndDateLessStartDate"), (endDate) => {
                        if (startDate && endDate) {
                            return endDate.isAfter(startDate);
                        }
                        return false;
                    })
                });
            shape.enableOvertime = VS.string();
            if (enableOvertime) {
                shape.overtimeCount = VS.integerMinMax(0, JAVA_INTEGER_MAX);
                shape.overtime = overtimeCount === '0' ? VS.integerMinMax(0, 60) : VS.integerMax(60);
            } else {
                shape.overtimeCount = VS.any();
                shape.overtime = VS.any();
            }
        } else {
            shape.decisionTime = VS.integerMax(60)
        }

        shape.positions = VS.arrayOf(VS.object({
            criterias: VS.arrayOf(VS.lazy(criterion => {
                if (bidType !== AuctionItemBidType.RFX_CRITERIUM_VALUE && criterion.type === 'price') {
                    return VS.any();
                } else {
                    const textYesNoOrList = criterion.type === 'text' || criterion.type === 'list' || criterion.type === 'yesNo';
                    const critShape = {
                        step: textYesNoOrList || !criterion.biddable ? VS.any() : VS.numberPositiveNoZero(),
                        min: textYesNoOrList || !criterion.biddable ? VS.any() : VS.numberMin0(),
                        max: textYesNoOrList || !criterion.biddable ? VS.any() : VS.numberMin(criterion.min, 'max_value_min_value')
                    };
                    if (AuctionRule.JAPANESE === rule) {
                        critShape.name = VS.string().test('maxSubMinDivStep', Tr('difference_between_max_and_min_value_is_not_multiple_of_step'),
                            () => {
                                return  criterion.max == null || criterion.min == null || criterion.step == null ||
                                    ((criterion.max - criterion.min) / criterion.step) === Math.floor((criterion.max - criterion.min) / criterion.step);
                            })
                    }
                    return VS.object(critShape)
                }
            })),
            items: VS.arrayOf(VS.lazy(item => {
                if (bidType === AuctionItemBidType.RFX_CRITERIUM_VALUE || !!item.itemIdToCopyFrom) {
                    return VS.any();

                } else {
                    const itemShape = {
                        step: VS.numberPositiveNoZero(),
                        min: VS.numberMin0(),
                        max: VS.numberMin(item.min, 'max_value_min_value')
                    }
                    if (AuctionRule.JAPANESE === rule) {
                        itemShape.name = VS.string().test('maxSubMinDivStep', Tr('difference_between_max_and_min_value_is_not_multiple_of_step'),
                            () => {
                                return item.max == null || item.min == null || item.step == null ||
                                    ((item.max - item.min) / item.step) === Math.floor((item.max - item.min) / item.step);
                            })
                    }
                    return VS.object(itemShape)
                }
            }))
        }));
        return VS.object(shape);
    })

    const title = useMemo(() => (<>
            <span className={classes.bold}>{Tr('auction_create_popup_title')} </span>
            {preparedAuction && <>
                <span className={classes.italic}>{Tr('part_x', {x: preparedAuction.positions[0].order})}</span>:
                <span> {preparedAuction.positions[0].name}</span>
            </>}
        </>),
        // eslint-disable-next-line
        [preparedAuction]);

    return (
        <Dialog
            open
            dialogActions={<MultiButton buttons={getButtons()}/>}
            title={title}
            maxWidth='lg'
        >
            <GridBlock
                column
            >

                {working && <Loading/>}
                {!!error && <TypographyWithIcon Icon={ErrorOutlineIcon} color='error'>{error}</TypographyWithIcon>}
                {!!preparedAuction && <>

                    <Form
                        initialValues={{
                            ...preparedAuction
                        }}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                        id={formId}
                    >
                        {(formikProps) => (
                            <GridBlock
                                column
                            >
                                <GridBlock
                                    justify='space-between'
                                >
                                    <GridBlock
                                        column
                                    >
                                        <Select
                                            className={classes.formElement}
                                            disabled
                                            label={Tr('auction_type')}
                                            id='type'
                                            required
                                            formikProps={formikProps}
                                            items={[createSelectItem('', '---'), ...AuctionTypeItems]}
                                        />
                                        <Select
                                            className={classes.formElement}
                                            disabled={working}
                                            label={Tr('auction_rule')}
                                            id='rule'
                                            required
                                            formikProps={formikProps}
                                            items={[createSelectItem('', '---'), ...AuctionTypeRuleItems(preparedAuction.rules)]}
                                            onChange={(event) => formikProps.setFieldValue('positions.0.bidType', AuctionItemBidTypeItems(event.target.value, preparedAuction.allowQuantityEditInOffer, preparedAuction.fixedOfferVat, formikProps.values.positions[0].items?.some(x => !!x.optional), preparedAuction.positions[0].items?.length)[0]?.value)}
                                        />
                                        <DateTimePicker
                                            className={classes.formElement}
                                            id='startDate'
                                            label={Tr('auction_start_date')}
                                            formikProps={formikProps}
                                            disabled={working}
                                            onChange={(value) => handleEndDate(formikProps, value)}
                                            minDate={new Date()}
                                        />

                                        {AuctionRule.JAPANESE === formikProps.values.rule ?
                                            <TextInput
                                                className={classes.formElement}
                                                disabled={working}
                                                label={Tr('auction_decision_time')}
                                                id='decisionTime'
                                                required
                                                formikProps={formikProps}
                                                type={INPUT_TYPE.INTEGER_POSITIVE_AMOUNT}
                                            />
                                            :
                                            <DateTimePicker
                                                className={classes.formElement}
                                                id='endDate'
                                                label={Tr('auction_end_date')}
                                                formikProps={formikProps}
                                                disabled={working}
                                                minDate={new Date()}
                                            />
                                        }

                                        <Select
                                            className={classes.formElement}
                                            disabled={working}
                                            label={Tr('auction_item_bid_type')}
                                            id='positions.0.bidType'
                                            required
                                            formikProps={formikProps}
                                            items={AuctionItemBidTypeItems(formikProps.values.rule, preparedAuction.allowQuantityEditInOffer, preparedAuction.fixedOfferVat, formikProps.values.positions[0].items?.some(x => !!x.optional), preparedAuction.positions[0].items?.length)}
                                        />
                                    </GridBlock>
                                    <GridBlock
                                        column
                                    >
                                        {getObjectValue('priceType', formikProps.values, "") === PriceType.NET_GROSS ?
                                            <Select
                                                className={classes.formElement}
                                                label={Tr('auction_price_type')}
                                                id='useNetOverGross'
                                                required
                                                formikProps={formikProps}
                                                items={PriceTypeItemsForAuction}
                                                onChange={(value) => handleUseNetOverGrossChange(formikProps, value)}
                                            />
                                            :
                                            <Select
                                                className={classes.formElement}
                                                disabled
                                                label={Tr('auction_price_type')}
                                                id='priceType'
                                                required
                                                formikProps={formikProps}
                                                items={PriceTypeItems}
                                            />
                                        }
                                        {AuctionRule.JAPANESE !== formikProps.values.rule &&
                                        <>
                                            <Select
                                                className={classes.formElement}
                                                disabled={working}
                                                label={Tr('auction_digital_sign')}
                                                id='digitalSign'
                                                required
                                                formikProps={formikProps}
                                                items={BooleanItems}
                                            />
                                            <div> 
                                                <Select
                                                    className={classes.formElement}
                                                    disabled={working}
                                                    label={Tr('auction_enable_overtime')}
                                                    id='enableOvertime'
                                                    required
                                                    formikProps={formikProps}
                                                    items={[createSelectItem('', '---'), ...BooleanItems]}
                                                    onChange={(e) => handleEnableOvertimeChange(formikProps, e)}
                                                />
                                                <TextInput
                                                    className={clsx(
                                                            classes.formElement,
                                                            classes.hidden)}
                                                    disabled={working}
                                                    label={Tr('auction_overtime_count')}
                                                    id='overtimeCount'
                                                    required                                        
                                                    formikProps={formikProps}
                                                    type={INPUT_TYPE.INTEGER_POSITIVE_AMOUNT}
                                                />
                                            </div>
                                            {formikProps.values.enableOvertime === true &&
                                                <TextInput
                                                    className={classes.formElement}
                                                    disabled={working}
                                                    label={Tr('auction_overtime')}
                                                    id='overtime'
                                                    required
                                                    formikProps={formikProps}
                                                    type={INPUT_TYPE.INTEGER_POSITIVE_AMOUNT}
                                                />
                                            }
                                            
                                        </>
                                        }
                                        <div className={classes.formElement}>
                                            <Typography component='span'>{Tr('auction_price_item_count')}</Typography>:
                                            <Typography component='span'> {formikProps.values.positions[0].priceItemCount}</Typography>
                                            <br/>
                                            <Typography component='span'>{Tr('auction_offer_count')}</Typography>:
                                            <Typography component='span'> {formikProps.values.positions[0].offerCount}</Typography>
                                            <br/>
                                            <Typography component='span'>{Tr('realization_value_all')}</Typography>:
                                            <Typography
                                                component='span'> {formikProps.values.positions[0].estimatedValue ? formikProps.values.positions[0].estimatedValue.toFixed(2).replace(/[.]/, ",") : '--'} PLN</Typography>
                                        </div>
                                    </GridBlock>
                                </GridBlock>

                                <OfferChoiceCreateAuctionTable
                                    demandId={demandId}
                                    items='criterias'
                                    working={working}
                                    formikProps={formikProps}
                                />

                                <OfferChoiceCreateAuctionTable
                                    demandId={demandId}
                                    items='items'
                                    working={working}
                                    formikProps={formikProps}
                                />

                            </GridBlock>
                        )}
                    </Form>
                </>}
            </GridBlock>
        </Dialog>)
}

OfferChoiceCreateAuctionPopup.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    roundId: PropTypes.number.isRequired,
    auctionType: PropTypes.string.isRequired
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(OfferChoiceCreateAuctionPopup)